/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { OpenInNew, Cancel } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import moment from 'moment';

const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
}));

const Validation = styled(Typography)(({ theme, state }) => ({
  color: state === '1' || state === 1 ? theme.palette.success.main : state === '2' || state === 2 ? theme.palette.info.main : theme.palette.error.main,
  fontWeight: 500,
}));

// Control status constants
const CONTROL_STATUS = {
  ACTIVE: 1,
  CANCELED: 2,
};

ControlDetailsPreview.propTypes = {
  control: PropTypes.object.isRequired,
  onViewFullDetails: PropTypes.func,
  onCancel: PropTypes.func,
  controllerColors: PropTypes.object,
  mainColor: PropTypes.string,
  inline: PropTypes.bool,
  isOverdue: PropTypes.bool,
};

/**
 * Control Details Preview for Betterfield
 * Renders inspection summary in calendar modal or inline in list
 * @param {boolean} inline - When true, renders in compact mode without close button
 * @param {boolean} isOverdue - When true, shows a late/overdue badge
 */
export default function ControlDetailsPreview({
  control,
  onViewFullDetails,
  onCancel,
  controllerColors = {},
  mainColor = '#1976d2',
  inline = false,
  isOverdue = false,
}) {
  const { t } = useTranslation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleCancelClick = (e) => {
    if (e) e.stopPropagation();
    setConfirmDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
    if (onCancel) onCancel();
  };

  const controllerName = control.controller
    ? `${control.controller.firstName} ${control.controller.lastName}`
    : t('calendar.unassigned');

  const controllerColor = control.controller
    ? controllerColors[control.controller.id] || '#757575'
    : '#757575';

  const formatDate = (dateTime, date) => {
    if (dateTime) {
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    }
    if (date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    }
    return 'N/A';
  };

  const getConformityLabel = (conformity) => {
    if (conformity === true || conformity === 1) {
      return t('inspections.info-card.complaint');
    }
    if (conformity === 2) {
      return t('inspections.items.scheduled');
    }
    return t('inspections.info-card.non-complaint');
  };

  const getConformityState = (conformity) => {
    if (conformity === true || conformity === 1) {
      return '1';
    }
    if (conformity === 2) {
      return '2';
    }
    return '0';
  };

  // Spacing based on inline mode
  const spacing = inline ? 1 : 1.5;
  const labelVariant = inline ? 'caption' : 'body2';
  const valueVariant = inline ? 'body2' : 'body1';

  const isCanceled = control.status === CONTROL_STATUS.CANCELED;

  return (
    <Box sx={{ pt: inline ? 1 : 0 }}>
      {/* Status Badges */}
      {(isOverdue || isCanceled) && (
        <Box sx={{ mb: spacing, display: 'flex', gap: 1 }}>
          {isCanceled ? (
            <Chip size="small" label={t('calendar.status.canceled')} color="default" />
          ) : isOverdue ? (
            <Chip size="small" label={t('calendar.status.late')} color="error" />
          ) : null}
        </Box>
      )}
      <Grid container spacing={inline ? 1 : 3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          {/* Date */}
          <Box sx={{ mb: spacing }}>
            <Label variant={labelVariant}>
              {`${t('inspections.items.date')}:`}
            </Label>
            <Typography variant={valueVariant}>
              {formatDate(control.dateTime, control.date)}
            </Typography>
          </Box>

          {/* Controller/Employee */}
          <Box sx={{ mb: spacing }}>
            <Label variant={labelVariant}>
              {`${t('inspections.info-card.employee')}:`}
            </Label>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: inline ? 12 : 14,
                  height: inline ? 12 : 14,
                  borderRadius: 0.5,
                  backgroundColor: controllerColor,
                }}
              />
              <Typography variant={valueVariant}>{controllerName}</Typography>
            </Box>
          </Box>

          {/* State/Conformity */}
          <Box sx={{ mb: spacing }}>
            <Label variant={labelVariant}>
              {`${t('inspections.info-card.state')}:`}
            </Label>
            <Validation state={getConformityState(control.conformity)} variant={valueVariant}>
              {getConformityLabel(control.conformity)}
            </Validation>
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          {/* Resource */}
          {control.resource && (
            <Box sx={{ mb: spacing }}>
              <Label variant={labelVariant}>
                {`${t('inspections.items.resource')}:`}
              </Label>
              <Typography variant={valueVariant}>
                {control.resource.name || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Resource Type */}
          {control.resource?.resourceType && (
            <Box sx={{ mb: spacing }}>
              <Label variant={labelVariant}>
                {`${t('inspections.items.resource-type')}:`}
              </Label>
              <Typography variant={valueVariant}>
                {control.resource.resourceType.name || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Flow */}
          {control.flow && (
            <Box sx={{ mb: spacing }}>
              <Label variant={labelVariant}>
                {`${t('inspections.items.flow-type')}:`}
              </Label>
              <Typography variant={valueVariant}>
                {control.flow.name || 'N/A'}
              </Typography>
            </Box>
          )}

          {/* Location (if available) */}
          {control.location && (
            <Box sx={{ mb: spacing }}>
              <Label variant={labelVariant}>
                {`${t('calendar.day-events.location')}:`}
              </Label>
              <Typography variant={valueVariant}>
                {control.location.name || 'N/A'}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Action Buttons - different layout for inline mode */}
      {inline ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          {isOverdue && !isCanceled && onCancel && (
            <Button
              variant="text"
              size="small"
              color="error"
              startIcon={<Cancel fontSize="small" />}
              onClick={handleCancelClick}
            >
              {t('calendar.control-details.cancel')}
            </Button>
          )}
          {onViewFullDetails && (
            <Button
              variant="text"
              size="small"
              endIcon={<OpenInNew fontSize="small" />}
              onClick={(e) => {
                e.stopPropagation();
                onViewFullDetails();
              }}
              sx={{ color: mainColor }}
            >
              {t('calendar.control-details.view-full-details')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {isOverdue && !isCanceled && onCancel && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={handleCancelClick}
              >
                {t('calendar.control-details.cancel')}
              </Button>
            )}
            {onViewFullDetails && (
              <Button
                variant="contained"
                endIcon={<OpenInNew />}
                onClick={onViewFullDetails}
                sx={{ backgroundColor: mainColor, '&:hover': { backgroundColor: mainColor, filter: 'brightness(0.9)' } }}
              >
                {t('calendar.control-details.view-full-details')}
              </Button>
            )}
          </Box>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>{t('calendar.cancel.confirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('calendar.cancel.confirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {t('common.no')}
          </Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>
            {t('common.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
